import type { ActionCodeSettings, AuthCredential, AuthError, UserCredential } from 'firebase/auth';
import {
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';

import { extractSpaceFromClaims, ssoTenantIDForEmail } from '@/utils/auth-helpers';
import { auth, deleteJWTCookie, setJWTCookie } from '@/utils/firebase-helpers';

import type { UserClaims } from './types';

/**
 * Creates Firebase ActionCodeSettings for email verification and password reset flows.
 * Automatically detects the current environment and constructs the appropriate redirect URL.
 *
 * For password reset: Firebase will show their hosted page for security, then redirect to this URL.
 * The hosted page can be customized in Firebase Console under Authentication > Templates.
 *
 * @param redirectPath - The path to redirect to after the action (e.g., '/auth/signin')
 * @returns ActionCodeSettings object configured for the current environment
 */
function createActionCodeSettings(redirectPath: string): ActionCodeSettings {
  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.NEXT_PUBLIC_APP_URL ?? 'https://editor.how.fm');

  return {
    url: `${baseUrl}${redirectPath}`,
    handleCodeInApp: false,
  };
}

/**
 * Signs out the current user.
 * Clears both Firebase auth state and JWT cookie simultaneously.
 *
 * @throws {FirebaseError} If sign-out fails
 * @see https://firebase.google.com/docs/auth/web/auth-state-persistence
 */
export async function signOut(): Promise<void> {
  const authInstance = await auth();
  await authInstance.signOut();

  deleteJWTCookie();
}

/**
 * Signs in a user with Google OAuth provider using a popup window.
 * After successful authentication, sets a JWT cookie for server-side session management.
 *
 * @returns UserCredential containing the authenticated user and additional metadata
 * @throws {FirebaseError} If sign-in fails (e.g., popup-closed-by-user, network-request-failed)
 * @see https://firebase.google.com/docs/auth/web/google-signin
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  const authInstance = await auth();
  const provider = new GoogleAuthProvider();

  provider.addScope('profile');
  provider.addScope('email');

  const result = await signInWithPopup(authInstance, provider);

  // Force a token refresh so the JWT cookie carries the user's CURRENT custom
  // claims, not a cached token. A cached token can still hold access the user no
  // longer has (e.g. a space they were just removed from); without forcing a
  // refresh here that stale access is written into the server-trusted cookie on
  // login and the user keeps access they shouldn't.
  const idToken = await result.user.getIdToken(true);
  await setJWTCookie(idToken);

  const idTokenResult = await result.user.getIdTokenResult();
  const claims = idTokenResult.claims as UserClaims;

  let spaceId = extractSpaceFromClaims(claims);

  // Force token refresh to get latest custom claims if no space found
  if (spaceId === null) {
    const refreshedToken = await result.user.getIdToken(true);
    await setJWTCookie(refreshedToken);
    const refreshedResult = await result.user.getIdTokenResult(true);
    const refreshedClaims = refreshedResult.claims as UserClaims;
    spaceId = extractSpaceFromClaims(refreshedClaims);
  }

  return result;
}

/**
 * Signs in a user with Microsoft OAuth provider using a popup window.
 * After successful authentication, sets a JWT cookie for server-side session management.
 *
 * @returns UserCredential containing the authenticated user and additional metadata
 * @throws {FirebaseError} If sign-in fails (e.g., popup-closed-by-user, network-request-failed)
 * @see https://firebase.google.com/docs/auth/web/microsoft-oauth
 */
export async function signInWithMicrosoft(): Promise<UserCredential> {
  const authInstance = await auth();
  const provider = new OAuthProvider('microsoft.com');

  provider.addScope('email');
  provider.addScope('profile');

  const result = await signInWithPopup(authInstance, provider);

  // Force a token refresh so the JWT cookie carries the user's CURRENT custom
  // claims, not a cached token. A cached token can still hold access the user no
  // longer has (e.g. a space they were just removed from); without forcing a
  // refresh here that stale access is written into the server-trusted cookie on
  // login and the user keeps access they shouldn't.
  const idToken = await result.user.getIdToken(true);
  await setJWTCookie(idToken);

  const idTokenResult = await result.user.getIdTokenResult();
  const claims = idTokenResult.claims as UserClaims;

  const ssoTenantId = ssoTenantIDForEmail(result.user.email ?? undefined);
  let spaceId = ssoTenantId ?? extractSpaceFromClaims(claims);

  // Force token refresh to get latest custom claims if no space found
  if (spaceId === null || spaceId === undefined) {
    const refreshedToken = await result.user.getIdToken(true);
    await setJWTCookie(refreshedToken);
    const refreshedResult = await result.user.getIdTokenResult(true);
    const refreshedClaims = refreshedResult.claims as UserClaims;
    spaceId = ssoTenantIDForEmail(result.user.email ?? undefined) ?? extractSpaceFromClaims(refreshedClaims);
  }

  return result;
}

export async function sendPasswordResetLink(email: string): Promise<void> {
  const authInstance = await auth();
  const actionCodeSettings = createActionCodeSettings('/auth/signin');
  await sendPasswordResetEmail(authInstance, email, actionCodeSettings);
}

/**
 * Signs in an existing user with email and password.
 * After successful authentication, sets a JWT cookie for server-side session management.
 *
 * @param email - The user's email address
 * @param password - The user's password
 * @returns UserCredential containing the authenticated user and additional metadata
 * @throws {FirebaseError} If sign-in fails (e.g., user-not-found, wrong-password, invalid-email)
 * @see https://firebase.google.com/docs/auth/web/password-auth#sign_in_a_user_with_an_email_address_and_password
 */
export async function signInWithPassword(email: string, password: string): Promise<UserCredential> {
  const authInstance = await auth();
  const result = await signInWithEmailAndPassword(authInstance, email, password);

  // Force a token refresh so the JWT cookie carries the user's CURRENT custom
  // claims, not a cached token. A cached token can still hold access the user no
  // longer has (e.g. a space they were just removed from); without forcing a
  // refresh here that stale access is written into the server-trusted cookie on
  // login and the user keeps access they shouldn't.
  const idToken = await result.user.getIdToken(true);
  await setJWTCookie(idToken);

  const idTokenResult = await result.user.getIdTokenResult();
  const claims = idTokenResult.claims as UserClaims;

  const ssoTenantId = ssoTenantIDForEmail(email);
  let spaceId = ssoTenantId ?? extractSpaceFromClaims(claims);

  // Force token refresh to get latest custom claims if no space found
  if (spaceId === null || spaceId === undefined) {
    const refreshedToken = await result.user.getIdToken(true);
    await setJWTCookie(refreshedToken);
    const refreshedResult = await result.user.getIdTokenResult(true);
    const refreshedClaims = refreshedResult.claims as UserClaims;
    spaceId = ssoTenantIDForEmail(email) ?? extractSpaceFromClaims(refreshedClaims);
  }

  return result;
}

/**
 * Type guard to check if error contains email in customData
 */
function hasEmailInCustomData(error: AuthError): error is AuthError & { customData: { email: string } } {
  if (error.customData === undefined || error.customData === null) {
    return false;
  }

  if ('email' in error.customData === false) {
    return false;
  }

  if (typeof error.customData.email !== 'string' || error.customData.email === '') {
    return false;
  }

  return true;
}

/**
 * Type guard to check if error contains credential in customData
 * Firebase provides credential at runtime but TypeScript types don't expose it
 */
function hasCredentialInCustomData(
  error: AuthError,
): error is AuthError & { customData: { credential: AuthCredential } } {
  if (error.customData === undefined || error.customData === null) {
    return false;
  }

  if ('credential' in error.customData === false) {
    return false;
  }

  if (error.customData.credential === null || error.customData.credential === undefined) {
    return false;
  }

  return true;
}

/**
 * Handles authentication failures and attempts automatic account linking when appropriate.
 * When a user tries to sign in with a provider (e.g., Microsoft) but an account already exists
 * with a different provider (e.g., Google or Email), this function automatically links them.
 *
 * This follows the Firebase best practice for handling the 'auth/account-exists-with-different-credential' error.
 *
 * @param error - The authentication error from Firebase
 * @param attemptedCredential - The credential that was attempted (optional, for linking)
 * @returns UserCredential if account linking succeeds, null otherwise
 * @throws {AuthError} If account linking fails or error is not linkable
 * @see https://firebase.google.com/docs/auth/web/account-linking
 */
export async function handleAuthFailureWithLinking(
  error: AuthError,
  attemptedCredential?: AuthCredential,
): Promise<UserCredential | null> {
  if (error.code !== 'auth/account-exists-with-different-credential') {
    throw error;
  }

  if (hasEmailInCustomData(error) === false) {
    throw error;
  }

  const email = error.customData.email;

  let credential: AuthCredential | undefined = attemptedCredential;

  if (credential === undefined) {
    if (hasCredentialInCustomData(error) === false) {
      throw error;
    }
    credential = error.customData.credential;
  }

  if (credential.providerId !== 'microsoft.com') {
    throw error;
  }

  const authInstance = await auth();

  const signInMethods = await fetchSignInMethodsForEmail(authInstance, email);

  if (signInMethods.length === 0) {
    throw error;
  }

  let originalProvider: GoogleAuthProvider | null = null;

  if (signInMethods.includes(GoogleAuthProvider.PROVIDER_ID) === true) {
    originalProvider = new GoogleAuthProvider();
    originalProvider.addScope('profile');
    originalProvider.addScope('email');
  } else if (signInMethods.includes(EmailAuthProvider.PROVIDER_ID) === true) {
    throw new Error('EMAIL_PASSWORD_SIGN_IN_REQUIRED');
  } else {
    throw error;
  }

  const originalResult = await signInWithPopup(authInstance, originalProvider);

  const linkedResult = await linkWithCredential(originalResult.user, credential);

  // Force-refresh so the cookie carries the user's current claims (see the
  // sign-in helpers above), not a cached token that could hold stale access.
  const idToken = await linkedResult.user.getIdToken(true);
  await setJWTCookie(idToken);

  return linkedResult;
}
